// Authentication.js

import React, { useState, useContext, useEffect } from 'react';
import { UserContext, ShowGettingStartedContext } from '../../objects/Context';
import './Authentication.css';
import logo from '../../assets/images/vairo-logo.png';
import { ReactComponent as EmailIcon } from '../../assets/icons/email-icon.svg';
import { ReactComponent as PasswordIcon } from '../../assets/icons/password-icon.svg';
import { ReactComponent as NameIcon } from '../../assets/icons/name-icon.svg';
import ConfirmationCode from './ConfirmCode';
import LoadingScreen from '../../components/LoadingScreen/LoadingScreen';
import { CognitoUserPool, CognitoUser } from 'amazon-cognito-identity-js';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { captureOAuthFlowFromUrl, getPostAuthDestination } from '../../utils/oauthFlow';

// Initialize userPool outside the component to avoid re-initialization on every render
const userPool = new CognitoUserPool({
  UserPoolId: process.env.REACT_APP_USER_POOL_ID,
  ClientId: process.env.REACT_APP_CLIENT_ID,
});

function Authentication() {
  const { setUser } = useContext(UserContext);
  const { setShowGettingStarted } = useContext(ShowGettingStartedContext);
  const [fullName, setFullName] = useState('');
  const [userId, setUserId] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [step, setStep] = useState(1);
  const [error, setError] = useState(null);
  const [login, setLogin] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize auth mode from query param: ?state=signup | login (default: login)
  useEffect(() => {
    const stateParam = (searchParams.get('state') || '').toLowerCase();
    if (stateParam === 'signup' || stateParam === 'register') {
      setLogin(false);
      setStep(1);
      setError(null);
    } else if (stateParam === 'login') {
      setLogin(true);
      setStep(1);
      setError(null);
    }
  }, [searchParams]);

  // Capture MCP OAuth flow id (from third-party authorize redirect) so it
  // survives the multi-step signup → confirmation → org-setup chain.
  useEffect(() => {
    captureOAuthFlowFromUrl(searchParams);
  }, [searchParams]);


  /////////////////// database transactions ////////////////////////
  const fetchUserData = async (userEmail) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/users/get-user-by-email/${userEmail}`, { withCredentials: true });
      const user = response.data[0];
      setUser(user);

      if (user.gettingStarted) {
        console.log('user getting started!!!!!');
        setShowGettingStarted(true);
      }

      navigate(getPostAuthDestination());
    } catch (error) {
      console.error('Error fetching user data:', error);
      setError('An error occurred while fetching user data. Please try again later.');
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  const signUpUser = async () => {
    if (fullName === '' || email === '' || password === '') {
      setError('Please fill in all fields.');
      return;
    }
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/users/sign-up`,
        {
          name: fullName,
          email: email,
          password: password,
        }
      );
      console.log('Sign up successful.');
      setUserId(response.data.userId);
      setStep(2);
    } catch (err) {
      if (err.response && err.response.data && err.response.data.error) {
        setError(err.response.data.error);
      } else {
        setError('An error occurred while signing up. Please try again later.');
      }
    } finally {
      setIsSubmitting(false);
      setLoading(false);
    }
  }

  const saveUserToWaitlist = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/internal/waitlist/save-user`, {
        name: fullName,
        email: email,
      });
    } catch (err) {
    } finally {
      return;
    }
  }

  const confirmEmail = async (code) => {
    if (email === '' || code === '') {
      setError('Please fill in all fields.');
      return;
    }
    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/users/confirm-email`, {
        email: email,
        code: code,
      });
      console.log('Email confirmed.');
      handleSignIn();
    } catch (err) {
      setError('An error occurred while confirming the email.');
      setLoading(false);
    } finally {
      setIsSubmitting(false);
    }
  }


  /////////////////// handlers ////////////////////////
  const handleSignIn = async () => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/auth/login`, {
        email: email,
        password: password,
      }, { 
        withCredentials: true
      });

      console.log('Sign in successful.');
      fetchUserData(email); // Fetch user data

    } catch (err) {
      console.error('Sign in error:', err.response?.data || err.message);

      if (err.response && err.response.data && err.response.data.error) {
        const errorMessage = err.response.data.error;

        if (errorMessage.includes("Email not confirmed")) {
          //setError("Your email is not confirmed. A new confirmation code has been sent to your email.");
          await sendConfirmationCode(); // Send a new confirmation code
        } else if (errorMessage.includes("Incorrect password")) {
          setError("Incorrect email or password. Please try again.");
        } else if (errorMessage.includes("Invalid credentials")) {
          setError("Invalid email or password.");
        } else {
          setError(errorMessage);
        }
      } else {
        setError("An unexpected error occurred. Please try again later.");
      }

      setLoading(false);
      setIsSubmitting(false);
    }
  };

  const sendConfirmationCode = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/users/resend-confirmation-code`, {
        email: email,
      });
      console.log('New confirmation code sent');
      setStep(2);
    } catch (err) {
      if (err.response && err.response.data && err.response.data.error) {
        setError(err.response.data.error);
      } else {
        setError('An error occurred while sending a new confirmation code.');
      }
    }
  };

  // Handle submission
  const handleSubmit = (e) => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }
    if (isSubmitting) return;
    setIsSubmitting(true);
    setLoading(true);
    setError(null);

    if (login && step === 1) {
      handleSignIn();
    } else {
      if (step === 1) {
        signUpUser();
      } else if (step === 2) {
        confirmEmail(e);
      } else if (login && step === 3) {
        submitForgotPassword();
      } else if (login && step === 4) {
        confirmPasswordReset(e);
      }
    }
  };

  const handleForgotPassword = () => {
    setEmail('');
    setNewPassword('');
    setPassword('');
    setStep(3);
  }

  const handleBackToSignIn = () => {
    setEmail('');
    setNewPassword('');
    setPassword('');
    setStep(1);
  }

  const submitForgotPassword = () => {
    if (email === '' || newPassword === '') {
      setError('Please fill in all fields.');
      return;
    }
    setLoading(true);
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });
    cognitoUser.forgotPassword({
      onSuccess: (data) => {
        console.log('Password reset initiated.');
        setStep(4);
        setLoading(false);
        setError(null);
        setIsSubmitting(false);
      },
      onFailure: (err) => {
        console.error('Error initiating password reset:', err);
        setError(err.message || 'An error occurred while initiating password reset.');
        setLoading(false);
        setIsSubmitting(false);
      },
    });
  };

  const confirmPasswordReset = (code) => {
    if (code === '' || newPassword === '' || email === '') {
      setError('Please fill in all fields.');
      return;
    }
    setLoading(true);
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });  
    cognitoUser.confirmPassword(code, newPassword, {
      onSuccess: () => {
        console.log('Password reset successful.');
        setLoading(false);
        setStep(1);
        setEmail('');
        setNewPassword('');
        setPassword('');
        setError(null);
        setIsSubmitting(false);
      },
      onFailure: (err) => {
        console.error('Error resetting password:', err);
        setError(err.message || 'An error occurred while resetting the password.');
        setLoading(false);
        setIsSubmitting(false);
      },
    });
  };


  return (
    <div className="login-container">
      {loading && <LoadingScreen isLoading={loading} />}          
          <div className="content">
            <img src={logo} alt="Vairo Logo" className="logo" />
            <h2>
              {login && step === 1
                ? 'Welcome back!'
                : step === 1
                ? 'Welcome to Vairo'
                : step === 2
                ? 'Verify Your Email'
                : step === 3
                ? 'Reset Your Password'
                : step === 4
                ? 'Confirm Password Reset'
                : ''}
            </h2>
            <div className="dots">
              <div className="dot" />
              <div className="dot" />
              <div className="dot" />
            </div>
            <p>
              {step === 1
                ? login
                  ? 'Please enter your email and password associated with your account.'
                  : 'To begin using Vairo, please enter your full name, email, and password.'
                : step === 2
                ? 'Enter the verification code sent to your email.'
                : step === 3
                ? 'Enter the verification code sent to your email and your new password.'
                : ''}
            </p>

            {/* Step 1: Sign Up Form */}
            {step === 1 && (
              <form className="form" onSubmit={handleSubmit}>
                {!login && (
                  <div className="input-wrapper">
                    <NameIcon className="email-icon" />
                    <input
                      type="text"
                      className="input"
                      placeholder="Full name"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      required
                    />
                  </div>
                )}
                <div className="input-wrapper">
                  <EmailIcon className="email-icon" />
                  <input
                    type="email"
                    className="input"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="input-wrapper">
                  <PasswordIcon className="email-icon" />
                  <input
                    type="password"
                    className="input"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  {login && <button type="button" className="forgot-password" onClick={() => {handleForgotPassword()}}>Forgot Password?</button>}
                </div>
                <button
                  type="submit"
                  className="submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Processing...' : login ? 'Log In' : 'Sign Up'}
                </button>
              </form>
            )}

            {/* Step 2: Email Confirmation */}
            {step === 2 && (
              <ConfirmationCode onConfirm={handleSubmit} />
            )}

            {/* Step 3: Password Reset */}
            {step === 3 && (
              <form className="form" onSubmit={handleSubmit}>
                <div className="input-wrapper">
                  <EmailIcon className="email-icon" />
                  <input
                    type="email"
                    className="input"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="input-wrapper">
                  <PasswordIcon className="email-icon" />
                  <input
                    type="password"
                    className="input"
                    placeholder="New Password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                  />
                  {login && <button type="button" className="forgot-password" onClick={() => {handleBackToSignIn()}}>Back to Sign In</button>}
                </div>
                <button
                  type="submit"
                  className="submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Processing...' : 'Confirm'}
                </button>
              </form>
            )}

            {/* Step 4: Password Reset Confirmation */}
            {step === 4 && (
              <ConfirmationCode onConfirm={handleSubmit} />
            )}

            {error && <p className="error-message">{error}</p>}

            {step===1 && (
              <button className="toggle-auth-mode" onClick={() => {
                // Reflect auth mode in URL: ?state=signup | login
                const nextState = login ? 'signup' : 'login';
                setSearchParams({ state: nextState }, { replace: true });
              }}>
              {login ? (
                <>New to Vairo? <span>Create account</span></>
              ) : (
                <>Already have an account? <span>Log In</span></>
              )}
            </button>
            )}
            {step===1 && (
              <p className="support-text">
                By signing {login ? 'in' : 'up'}, you agree to Vairo's{' '}
                <a href="https://vairo.ai/privacy" target="_blank" rel="noopener noreferrer">Terms of Service</a> and{' '}
                <a href="https://vairo.ai/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
              </p>
            )}
          </div>
    </div>
  );
}

export default Authentication;